import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Wallet, TrendingUp, Receipt, Sparkles, Plus, ArrowRight } from "lucide-react";
import { getPnl, getPnlSummary, listExpenses, monthLabel, CATEGORY_LABEL } from "@/lib/finance";
import { formatInr } from "@/lib/format";
import { errorMessage } from "@/lib/api";

export default function FinanceOverviewPage() {
    const nav = useNavigate();
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [pnl, setPnl] = useState(null);
    const [series, setSeries] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [aiInput, setAiInput] = useState("");

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [p, s, e] = await Promise.all([
                    getPnl(year, month),
                    getPnlSummary(6),
                    listExpenses({ limit: 5 }),
                ]);
                setPnl(p);
                setSeries((s.series || []).map((r) => ({
                    name: monthLabel(r.year, r.month),
                    Revenue: (r.revenue_paise || 0) / 100,
                    Expenses: (r.expenses_paise || 0) / 100,
                    Net: (r.net_paise || 0) / 100,
                })));
                setExpenses(e.expenses || []);
            } catch (err) { toast.error(errorMessage(err)); }
            finally { setLoading(false); }
        })();
    }, [year, month]);

    const kpi = pnl ? {
        rev: pnl.revenue.total_paise, exp: pnl.expenses.total_paise,
        net: pnl.net_profit_paise, margin: pnl.margin_percent,
    } : { rev: 0, exp: 0, net: 0, margin: 0 };

    const sendAi = () => {
        if (!aiInput.trim()) return;
        nav(`/admin/finance/ai-accountant?message=${encodeURIComponent(aiInput)}`);
    };

    return (
        <div data-testid="finance-overview">
            <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-sm font-medium text-orange-600">Finance</div>
                    <h1 className="mt-1 text-3xl font-extrabold text-neutral-900">Overview</h1>
                    <p className="mt-1 text-sm text-neutral-500">Month-to-date revenue, expenses and AI insights.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                        <SelectTrigger className="w-28" data-testid="fin-month"><SelectValue /></SelectTrigger>
                        <SelectContent>{Array.from({ length: 12 }, (_, i) => (
                            <SelectItem key={i + 1} value={String(i + 1)}>{new Date(2000, i, 1).toLocaleDateString("en", { month: "long" })}</SelectItem>
                        ))}</SelectContent>
                    </Select>
                    <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                        <SelectTrigger className="w-24" data-testid="fin-year"><SelectValue /></SelectTrigger>
                        <SelectContent>{[year - 1, year, year + 1].map((y) => (
                            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                        ))}</SelectContent>
                    </Select>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Kpi label="MTD Revenue" value={formatInr(kpi.rev)} icon={TrendingUp} tone="orange" testid="kpi-rev" />
                <Kpi label="MTD Expenses" value={formatInr(kpi.exp)} icon={Receipt} tone="rose" testid="kpi-exp" />
                <Kpi label="Net Profit" value={formatInr(kpi.net)} icon={Wallet} tone={kpi.net >= 0 ? "emerald" : "rose"} testid="kpi-net" />
                <Kpi label="Margin" value={kpi.margin == null ? "—" : `${kpi.margin}%`} icon={Sparkles} tone="blue" testid="kpi-margin" />
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-2">
                <Card className="rounded-2xl border border-neutral-200 p-4">
                    <div className="flex items-center justify-between">
                        <div className="text-sm font-bold">Recent Expenses</div>
                        <Link to="/admin/finance/expenses"><Button size="sm" variant="ghost" data-testid="record-expense-link"><Plus size={12} className="mr-1" /> Record Expense</Button></Link>
                    </div>
                    <ul className="mt-2 divide-y divide-neutral-100" data-testid="recent-exp-list">
                        {loading ? Array.from({ length: 5 }).map((_, i) => <li key={i} className="py-2"><Skeleton className="h-6 w-full" /></li>)
                            : expenses.slice(0, 5).map((e) => (
                                <li key={e.id} className="flex items-center justify-between py-2 text-sm">
                                    <div><b>{CATEGORY_LABEL[e.category]}</b> · <span className="text-neutral-500">{e.description}</span></div>
                                    <span className="font-mono">{formatInr(e.amount_paise)}</span>
                                </li>
                            ))}
                    </ul>
                </Card>

                <Card className="rounded-2xl border border-neutral-200 p-4">
                    <div className="mb-2 flex items-center gap-2"><Sparkles size={16} className="text-orange-600" /><div className="text-sm font-bold">AI Accountant</div></div>
                    <Input value={aiInput} onChange={(e) => setAiInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && sendAi()}
                        placeholder="Ask a question about your finances…"
                        data-testid="ai-quick-input" />
                    <div className="mt-2 flex gap-2">
                        <Button className="nest-gradient text-white" size="sm" onClick={sendAi} data-testid="ai-quick-send">Ask <ArrowRight size={12} className="ml-1" /></Button>
                        <Link to={`/admin/finance/ai-accountant?action=monthly-summary&year=${year}&month=${month}`}>
                            <Button variant="outline" size="sm" data-testid="ai-gen-report">Generate this month's report</Button>
                        </Link>
                    </div>
                </Card>
            </div>

            <Card className="mt-6 rounded-2xl border border-neutral-200 p-4">
                <div className="text-sm font-bold">6-Month Trend</div>
                <div className="h-72">
                    {loading ? <Skeleton className="h-full w-full" /> : (
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={series}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                                <XAxis dataKey="name" fontSize={12} />
                                <YAxis fontSize={12} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                                <Tooltip formatter={(v) => `₹${Math.round(v).toLocaleString("en-IN")}`} />
                                <Legend />
                                <Line type="monotone" dataKey="Revenue" stroke="#f97316" strokeWidth={2} />
                                <Line type="monotone" dataKey="Expenses" stroke="#e11d48" strokeWidth={2} />
                                <Line type="monotone" dataKey="Net" stroke="#059669" strokeWidth={2} />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </Card>
        </div>
    );
}

function Kpi({ label, value, icon: Icon, tone, testid }) {
    const cls = { orange: "from-orange-500 to-amber-400", emerald: "from-emerald-600 to-emerald-400", rose: "from-rose-600 to-rose-400", blue: "from-blue-600 to-blue-400" }[tone];
    return (
        <Card className={`rounded-2xl border-0 bg-gradient-to-br ${cls} p-4 text-white`} data-testid={testid}>
            <div className="flex justify-between text-xs uppercase opacity-90"><span>{label}</span><Icon size={16} /></div>
            <div className="mt-2 text-2xl font-extrabold">{value}</div>
        </Card>
    );
}
